<script lang="ts">
  import { onDestroy, onMount } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import FormWrapper, { type FormContext } from "@kontextwerk/web-sdk/ui/layout/FormWrapper.svelte"
  import { validateField, validateInput } from "@kontextwerk/web-sdk/core/forms/formValidation"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiClose, mdiContentSave, mdiPlus } from "@mdi/js"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import {
    classifyPhoneNumber,
    createContactPoint,
    updateContactPoint,
  } from "../../../../../../../../../../../../../../api/kehrblatt/customer-registry/contact-points"
  import {
    emptyContactAddress,
    isValidContactAddress,
    parseContactAddress,
    serializeContactAddress,
    type ContactAddressValue,
  } from "../../../../../../../../../../../../_shared/_logic/contact-addresses"
  import { DACH_COUNTRY_OPTIONS } from "../../../../../../../../../../../../../../_shared/_logic/countries"
  import {
    contactPointKindOptions,
    isPhoneContactPointKind,
  } from "../../../../../../../../../../../../_shared/_logic/contact-point-kinds"
  import {
    contactChannelOptionsForKind,
    defaultEnabledChannelsForKind,
    enabledChannelSelectionChanged,
    enabledChannelsForContactPoint,
    normalizeEnabledChannelsForKind,
  } from "../../_shared/_logic/contact-channels"
  import type { Snippet } from "svelte"

  type OverlayHostProps = {
    onremoveOverlay?: () => void
    onshowOverlay?: () => void
    setActionButtonSnippet?: (snippet: Snippet | null) => void
  }

  type Props = {
    organizationId: string
    customerId: string
    contact?: ContactPoint | null
    onSaved?: (contact: ContactPoint) => void | Promise<void>
  }

  type EditableContactPointSource = "MANUAL" | "IMPORT" | "INBOUND_LEARNED"

  type ContactFormModel = {
    kind: ContactPointKind
    label: string
    value: string
    address: ContactAddressValue
    verificationStatus: "VERIFIED" | "UNVERIFIED"
    source: EditableContactPointSource
    enabledChannels: ContactChannel[]
  }

  const contactFieldIds = {
    kind: "contact-kind",
    label: "contact-label",
    value: "contact-value",
    street: "contact-address-street",
    houseNumber: "contact-address-house-number",
    zip: "contact-address-zip",
    city: "contact-address-city",
    country: "contact-address-country",
    addressExtra: "contact-address-extra",
    isVerified: "contact-verified",
    source: "contact-source",
    enabledChannels: "contact-enabled-channels",
  }

  let {
    organizationId,
    customerId,
    contact = null,
    onSaved = null,
    onremoveOverlay = null,
    onshowOverlay = null,
    setActionButtonSnippet = null,
  }: Props & OverlayHostProps = $props()

  const resetContactForm = (): ContactFormModel => ({
    kind: "EMAIL",
    label: "",
    value: "",
    address: emptyContactAddress(),
    verificationStatus: "UNVERIFIED",
    source: "MANUAL",
    enabledChannels: defaultEnabledChannelsForKind("EMAIL"),
  })

  const editableContactSource = (source?: string | null): EditableContactPointSource =>
    source === "MANUAL" || source === "IMPORT" ? source : "INBOUND_LEARNED"

  const getInitContact = (): ContactFormModel =>
    contact
      ? {
          kind: contact.kind,
          label: contact.label ?? "",
          value: contact.value,
          address:
            contact.kind === "ADDRESS" ? parseContactAddress(contact.value) : emptyContactAddress(),
          verificationStatus: contact.is_verified ? "VERIFIED" : "UNVERIFIED",
          source: editableContactSource(contact.source),
          enabledChannels: enabledChannelsForContactPoint(contact),
        }
      : resetContactForm()

  const resetChannelsForKind = (data: ContactFormModel) => {
    data.enabledChannels = defaultEnabledChannelsForKind(data.kind)
  }

  let classificationRequest = 0
  let classificationPromise: Promise<void> | null = null
  let classificationPending = $state(false)
  let classificationHint = $state<string | null>(null)
  let phoneKindManuallySelected = false
  let channelSelectionManuallyChanged = false

  const inputValue = (value: unknown) => {
    if (value instanceof Event) {
      const target = value.target as HTMLInputElement | HTMLTextAreaElement | null

      return `${target?.value ?? ""}`
    }

    return `${value ?? ""}`
  }

  const handleKindChange = (data: ContactFormModel) => {
    classificationRequest += 1
    classificationPromise = null
    classificationPending = false
    classificationHint = null
    resetChannelsForKind(data)
    channelSelectionManuallyChanged = false
    phoneKindManuallySelected = isPhoneContactPointKind(data.kind)
  }

  const classifyChangedPhone = async (data: ContactFormModel, rawValue: unknown) => {
    const value = inputValue(rawValue).trim()
    data.value = value
    const requestId = ++classificationRequest

    if (!value || !isPhoneContactPointKind(data.kind)) {
      classificationPending = false
      classificationHint = null

      return
    }

    classificationPending = true
    try {
      const result = await classifyPhoneNumber(value)

      if (requestId !== classificationRequest || data.value.trim() !== value) return

      if (!phoneKindManuallySelected && data.kind !== result.suggested_kind) {
        data.kind = result.suggested_kind
        resetChannelsForKind(data)
        channelSelectionManuallyChanged = false
      }

      classificationHint =
        result.confidence === "HIGH"
          ? `Erkannt als ${result.suggested_kind === "MOBILE" ? "Mobilnummer" : "Festnetznummer"}.`
          : "Nicht eindeutig – Festnetz ist vorausgewählt. Bitte prüfen."
    } catch {
      if (requestId === classificationRequest) classificationHint = null
    } finally {
      if (requestId === classificationRequest) classificationPending = false
    }
  }

  const queuePhoneClassification = (data: ContactFormModel, rawValue: unknown) => {
    classificationPromise = classifyChangedPhone(data, rawValue)
    void classificationPromise
  }

  const close = (submitting: boolean) => {
    if (submitting) return

    onremoveOverlay?.()
  }

  const isPlausiblePhone = (value: string): boolean => {
    const trimmed = value.trim()

    if (!trimmed) return false

    if (!/^[\d+().\s-]+$/.test(trimmed)) return false

    const digitsOnly = trimmed.replace(/\D/g, "")

    return digitsOnly.length >= 6 && digitsOnly.length <= 15
  }

  const phoneErrorMessage =
    "Bitte eine gültige Telefonnummer angeben (mind. 6 Ziffern, nur Zahlen, +, (), -)."

  let submitting = $state(false)
  let submitForm = $state<(() => Promise<void>) | null>(null)

  const validateContact = ({ data }: { data: ContactFormModel; form: HTMLFormElement | null }) => {
    const trimmed = data.value.trim()

    if (isPhoneContactPointKind(data.kind)) {
      return validateField(contactFieldIds.value, trimmed, () =>
        isPlausiblePhone(trimmed) ? "" : phoneErrorMessage,
      )
    }

    if (data.kind === "ADDRESS") {
      return validateField(contactFieldIds.street, data.address.street.trim(), () =>
        isValidContactAddress(data.address) ? "" : "Bitte Straße, PLZ und Ort der Adresse angeben.",
      )
    }

    return validateField(contactFieldIds.value, trimmed, validateInput)
  }

  const saveContact = async (data: ContactFormModel) => {
    if (!organizationId || !customerId) return

    await classificationPromise
    const value =
      data.kind === "ADDRESS" ? serializeContactAddress(data.address) : data.value.trim()

    if (!value) {
      newNotification({ class: "error", html: "Bitte einen Wert für den Kontaktpunkt angeben." })

      return
    }

    if (data.kind === "ADDRESS" && !isValidContactAddress(data.address)) {
      newNotification({ class: "error", html: "Bitte Straße, PLZ und Ort der Adresse angeben." })

      return
    }

    if (isPhoneContactPointKind(data.kind) && !isPlausiblePhone(value)) {
      validateField(contactFieldIds.value, value, () => phoneErrorMessage)
      newNotification({ class: "error", html: phoneErrorMessage })

      return
    }

    try {
      const payload: ContactPointCreatePayload = {
        kind: data.kind,
        label: data.label.trim() || null,
        value,
        is_verified: data.verificationStatus === "VERIFIED",
        source: data.source,
        customer_id: customerId,
        organization_id: organizationId,
      }
      const enabledChannels = normalizeEnabledChannelsForKind(data.kind, data.enabledChannels)

      if (
        channelSelectionManuallyChanged &&
        enabledChannelSelectionChanged(contact, data.kind, enabledChannels)
      ) {
        payload.enabled_channels = enabledChannels
      }

      const updated = contact
        ? await updateContactPoint(contact.id, payload)
        : await createContactPoint(payload)
      await onSaved?.(updated)
      newNotification({ class: "success", html: "Kontaktpunkt wurde gespeichert." })
      onremoveOverlay?.()
    } catch (err) {
      console.error("contact_point_save_failed", err)
      newNotification({ class: "error", html: "Kontaktpunkt konnte nicht gespeichert werden." })
    }
  }

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(contactActions)
  })

  onDestroy(() => {
    setActionButtonSnippet?.(null)
  })
</script>

<div class="overlay-form form light">
  <FormWrapper
    theme="light"
    mode={contact ? "edit" : "create"}
    getInitObject={getInitContact}
    validateData={validateContact}
    onSubmit={saveContact}
    submitLabel={contact ? "Speichern" : "Anlegen"}
    showReset={false}
    reinitKey={contact?.id ?? "__new__"}
    formSnippet={contactFormContent}
    bind:submitting
    setSubmitFn={(fn) => (submitForm = fn)}
  >
    {#snippet submitButtonSnippet()}
      <Icon path={contact ? mdiContentSave : mdiPlus} />
    {/snippet}
  </FormWrapper>
</div>

{#snippet contactFormContent(ctx: FormContext)}
  {@const { data, submitting } = ctx}
  <section class="row">
    <Input
      id={contactFieldIds.kind}
      type="select"
      placeholder="Typ"
      options={contactPointKindOptions}
      bind:value={data.kind}
      brightBackground={true}
      disabled={submitting}
      onChange={() => handleKindChange(data)}
    />
    <Input
      id={contactFieldIds.label}
      placeholder="Label (optional)"
      bind:value={data.label}
      disabled={submitting}
    />
  </section>
  {#if data.kind === "ADDRESS"}
    <section class="row">
      <Input
        id={contactFieldIds.street}
        placeholder="Straße"
        bind:value={data.address.street}
        disabled={submitting}
      />
      <Input
        id={contactFieldIds.houseNumber}
        placeholder="Hausnummer"
        bind:value={data.address.house_number}
        disabled={submitting}
      />
    </section>
    <section class="row">
      <Input
        id={contactFieldIds.zip}
        placeholder="PLZ"
        bind:value={data.address.zip}
        disabled={submitting}
      />
      <Input
        id={contactFieldIds.city}
        placeholder="Ort"
        bind:value={data.address.city}
        disabled={submitting}
      />
    </section>
    <section class="row">
      <Input
        id={contactFieldIds.addressExtra}
        placeholder="Adresszusatz (optional)"
        bind:value={data.address.address_extra}
        disabled={submitting}
      />
      <Input
        id={contactFieldIds.country}
        type="select"
        placeholder="Land"
        options={DACH_COUNTRY_OPTIONS}
        bind:value={data.address.country}
        brightBackground={true}
        disabled={submitting}
      />
    </section>
  {:else}
    <section class="row">
      <Input
        id={contactFieldIds.value}
        placeholder={data.kind === "EMAIL" ? "E-Mail" : "+49…"}
        bind:value={data.value}
        disabled={submitting}
        onChange={(value) => queuePhoneClassification(data, value)}
      />
    </section>
    {#if isPhoneContactPointKind(data.kind) && (classificationPending || classificationHint)}
      <p class="classification-hint" aria-live="polite">
        {classificationPending ? "Nummer wird geprüft …" : classificationHint}
      </p>
    {/if}
  {/if}
  <section class="row">
    <Input
      id={contactFieldIds.isVerified}
      type="select"
      placeholder="Verifizierung"
      options={[
        { name: "Ist verifiziert", value: "VERIFIED" },
        { name: "Ist nicht verifiziert", value: "UNVERIFIED" },
      ]}
      bind:value={data.verificationStatus}
      brightBackground={true}
      disabled={submitting}
    />
    <Input
      id={contactFieldIds.source}
      type="select"
      placeholder="Herkunft"
      options={[
        { name: "In KehrBlatt eingetragen", value: "MANUAL" },
        { name: "Aus Fachprogramm", value: "IMPORT" },
        { name: "Aus Rückmeldung übernommen", value: "INBOUND_LEARNED" },
      ]}
      bind:value={data.source}
      brightBackground={true}
      disabled={submitting}
    />
  </section>
  <section class="row">
    <div class="field-with-help">
      <Input
        id={contactFieldIds.enabledChannels}
        type="select"
        multiple={true}
        placeholder="Verwendbare Kanäle"
        options={contactChannelOptionsForKind(data.kind)}
        bind:value={data.enabledChannels}
        brightBackground={true}
        disabled={submitting}
        onChange={() => (channelSelectionManuallyChanged = true)}
      />
      <span class="field-help">Diese Kanäle darf KehrBlatt für diesen Kontaktpunkt verwenden.</span>
    </div>
  </section>
{/snippet}

{#snippet contactActions()}
  <CTA
    type="secondaryButton"
    content="Abbrechen"
    onClick={() => close(submitting)}
    disabled={submitting}
  >
    {#snippet iconSnippet()}
      <Icon path={mdiClose} />
    {/snippet}
  </CTA>
  <CTA
    type="primaryButton"
    content={submitting ? "Speichere…" : contact ? "Speichern" : "Anlegen"}
    onClick={() => submitForm?.()}
    disabled={submitting}
  >
    {#snippet iconSnippet()}
      <Icon path={contact ? mdiContentSave : mdiPlus} />
    {/snippet}
  </CTA>
{/snippet}

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .overlay-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding-bottom: 1rem;
    height: fit-content;
  }

  :global(.overlay-form .formActions) {
    display: none;
  }

  .field-with-help {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    width: 100%;
  }

  .field-help {
    color: #74808c;
    font-size: 0.82rem;
    line-height: 1.35;
  }

  .classification-hint {
    margin: -0.65rem 0 0;
    color: #74808c;
    font-size: 0.82rem;
    line-height: 1.35;
  }
</style>
